import { z } from "zod";
import { SystemEvent } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Scan Conversations Awaiting Reply Step
 *
 * The no-response half of agent-managed email conversations: finds
 * conversations still in awaiting_reply strictly longer than
 * `noResponseDays`, and dispatches each topic's no-response handling — for
 * collections a thread timeline entry, an AR dashboard task
 * (ar_conversation_no_response), and an in-app notification to the thread's
 * watchers. Each wait notifies once; a follow-up send re-arms it.
 * Conversations whose topic entity no longer needs tracking (e.g. the thread
 * was resolved) are closed instead.
 *
 * Pair with a schedule trigger (daily) — see the "Agent Conversation
 * No-Response Scan" template.
 */
export const scanConversationsAwaitingReplyStep = defineStepConfig({
  type: "scan_conversations_awaiting_reply",
  name: "Scan Conversations Awaiting Reply",
  description:
    "Finds agent-managed email conversations with no reply past the configured window and raises follow-up tasks + notifications",
  category: "Data Access",

  // No data inputs — just the implicit flow signal from the schedule trigger.
  inputSchema: z.object({}),

  outputSchema: z.object({
    overdueCount: z
      .number()
      .int()
      .nonnegative()
      .describe("Conversations found past the no-response window"),
    notifiedCount: z
      .number()
      .int()
      .nonnegative()
      .describe(
        "Conversations marked no_response with a task + notification raised",
      ),
    closedCount: z
      .number()
      .int()
      .nonnegative()
      .describe(
        "Overdue conversations closed because the topic entity no longer needs tracking",
      ),
  }),

  configSchema: z.object({
    noResponseDays: z
      .number()
      .int()
      .positive()
      .default(5)
      .describe(
        "Flag conversations with no reply STRICTLY longer than this many days after the send.",
      ),
  }),

  emits: [SystemEvent.TASK_CREATED] as const,

  execution: {
    startToCloseTimeout: "120 seconds",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
